import { App } from '../../App';
import { differenceInSeconds } from 'date-fns';
import {
  FINGERPRINT_CONSTANTS_KEYS,
  getFingerprintDeviceId,
} from '../../utils/fingerprintDeviceId';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { PhoneVerificationForm } from 'pages/PhoneVerification/PhoneVerification';
import { routes } from '../../routes';
import { useErrorManager } from 'InternalLibrary';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';

import React, { useState } from 'react';
import useIovation from '../../hooks/useIovation';

const getAttempt = () => {
  const attempt = sessionStorage.getItem('phoneVerification_attempt');
  return attempt ? Number(attempt) : 1;
};

const KEY_ATTEMPT = 'phoneVerification_attempt';

const saveAttempt = (attempt: number) => {
  sessionStorage.setItem(KEY_ATTEMPT, `${attempt}`);
};

const deleteSessionStorageAttempt = () => {
  sessionStorage.removeItem(KEY_ATTEMPT);
};

export function usePhoneVerificationScreenLogic() {
  const { setError } = useErrorManager();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const attempt = getAttempt();

  const { client, fetchClient } = useClient();
  const apiPutWebcodeVerify = App.useApi(
    'client/application/verifyWebCode',
    'PUT',
  );
  const { fetch: fetchtPutPhone } = App.useApi('client/phone', 'PUT');
  const { fetch: fetchtPostPhone } = App.useApi('client/phone', 'POST');

  const apiPostClientApplicationWebcode = App.useApi(
    'client/application/webcode',
    'POST',
  );

  const change_phone_text_1 = React.useMemo(() => {
    // TODO: when data.mobilePhoneVerified === true redirect further (in navigator)...
    return client?.mobilePhone?.mobilePhone;
  }, [client?.mobilePhone?.mobilePhone]);

  useIovation();

  const handleSubmit = async (data: PhoneVerificationForm) => {
    try {
      setLoading(true);
      const deviceId = await getFingerprintDeviceId(client?.personalId);
      await apiPutWebcodeVerify.fetch({
        headers: getFetchHeaders({
          [FINGERPRINT_CONSTANTS_KEYS.deviceId]: deviceId,
        }),
        body: {
          webCode: `${data.verificationCode0}${data.verificationCode1}${data.verificationCode2}${data.verificationCode3}`,
        },
      });

      deleteSessionStorageAttempt();

      navigate(routes.FirstLoan_PhoneVerification_Check);
    } catch (error) {
      setLoading(false);
      setError(error);

      if (attempt < 3) {
        saveAttempt(attempt + 1);
      } else {
        deleteSessionStorageAttempt();
        navigate(routes.FirstLoan_IncorrectCode);
      }
    }
  };

  async function resendCode(): Promise<number | undefined> {
    const sessionStorageLastSMSSent = sessionStorage.getItem('lastSMSSent');
    if (
      sessionStorageLastSMSSent &&
      !Number.isNaN(parseInt(sessionStorageLastSMSSent)) &&
      differenceInSeconds(
        new Date().getTime(),
        parseInt(sessionStorageLastSMSSent),
      ) < 60
    ) {
      return (
        60 -
        differenceInSeconds(
          new Date().getTime(),
          parseInt(sessionStorageLastSMSSent),
        )
      );
    }
    sessionStorage.setItem('lastSMSSent', new Date().getTime().toString());

    await apiPostClientApplicationWebcode.fetch({
      headers: getFetchHeaders(),
    });
    return 60;
  }

  async function updateClientPhone(newPhoneNumber: string): Promise<void> {
    try {
      // Update phone number on cola
      await fetchtPutPhone({
        body: {
          mobilePhone: newPhoneNumber,
        },
      });

      // Resend webcode without the 60 seconds limitation
      await fetchtPostPhone({
        body: {
          mobilePhone: newPhoneNumber,
        },
      });

      await fetchClient();
    } catch (e) {
      throw e;
    }
  }

  function navigateToChangePhone() {
    navigate(routes.FirstLoan_PhoneVerification_ChangePhone);
  }

  return {
    handleSubmit,
    resendCode,
    loading,
    change_phone_text_1,
    navigateToChangePhone,
    updateClientPhone,
  };
}
